'use client';

import React from 'react';
import Link from 'next/link';

interface DestinationCard {
  id: string;
  icon: string;
  label: string;
  arabic: string;
  description: string;
  href: string;
  accentColor: string;
  tag: string;
}

const DESTINATION_CARDS: DestinationCard[] = [
  {
    id: 'dashboard',
    icon: '◈',
    label: 'My Dashboard',
    arabic: 'لَوْحَة رِحْلَتِي',
    description: 'Your personal journey map — quests, saved collection, garment, badges, and resonance history.',
    href: '/user-dashboard',
    accentColor: '#c9a84c',
    tag: 'Personal',
  },
  {
    id: 'community',
    icon: '⬡',
    label: 'Community',
    arabic: 'الأُمَّة',
    description: 'Connect with caretakers, explore phases, share garments, and engage in collective recognition.',
    href: '/community',
    accentColor: '#2d8a87',
    tag: 'Collective',
  },
  {
    id: 'impact',
    icon: '✦',
    label: 'Impact Analytics',
    arabic: 'تَأْثِير الرِّعَايَة',
    description: 'Platform-wide milestones, cohort progress, and the collective Alhamdulilah state across all caretakers.',
    href: '/impact',
    accentColor: '#2d7a47',
    tag: 'Analytics',
  },
  {
    id: 'followers',
    icon: '◎',
    label: 'Followers & Cohorts',
    arabic: 'الأَتْبَاع وَالمَجْمُوعَات',
    description: 'Follow caretakers, join role-based cohorts, track community tasks, and coordinate local study collectives.',
    href: '/followers',
    accentColor: '#8a8ab8',
    tag: 'Network',
  },
  {
    id: 'roadmap',
    icon: '⌘',
    label: 'Roadmap',
    arabic: 'خَارِطَة الطَّرِيق',
    description: 'Platform development milestones, upcoming features, and the evolving architecture of the Haal Project.',
    href: '/user-dashboard?tab=roadmap',
    accentColor: '#D4A82A',
    tag: 'Platform',
  },
  {
    id: 'moderation',
    icon: '⬡',
    label: 'Moderation',
    arabic: 'الإِشْرَاف',
    description: 'Community moderation tools — review flagged content, manage caretaker roles, and uphold the Mizan.',
    href: '/admin-moderation',
    accentColor: '#7a4a2a',
    tag: 'Admin',
  },
];

export default function DestinationCardsSection() {
  return (
    <section style={{ background: '#060d0a', borderTop: '1px solid rgba(201,168,76,0.06)', borderBottom: '1px solid rgba(201,168,76,0.06)', padding: 'clamp(48px, 8vw, 96px) clamp(16px, 5vw, 48px)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 56px)' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#c9a84c', letterSpacing: '.25em', textTransform: 'uppercase', marginBottom: 12 }}>
            Platform Destinations
          </div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(22px, 4vw, 36px)', color: '#d4c5a9', marginBottom: 12, lineHeight: 1.3 }}>
            Where Will You Go?
          </h2>
          <p style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(13px, 2.5vw, 16px)', color: 'rgba(212,197,169,0.5)', maxWidth: 560, margin: '0 auto' }}>
            Six pathways through the Haal Project — each a different lens on the caretaker's work.
          </p>
        </div>

        {/* Cards grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'clamp(12px, 2vw, 20px)',
        }} className="destination-cards-grid">
          {DESTINATION_CARDS.map((card) => (
            <Link
              key={card.id}
              href={card.href}
              style={{
                display: 'block',
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${card.accentColor}22`,
                borderTop: `2px solid ${card.accentColor}55`,
                borderRadius: 8,
                padding: 'clamp(20px, 3vw, 28px)',
                textDecoration: 'none',
                transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.15s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = `${card.accentColor}08`;
                el.style.borderColor = `${card.accentColor}44`;
                el.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = 'rgba(255,255,255,0.02)';
                el.style.borderColor = `${card.accentColor}22`;
                el.style.transform = 'translateY(0)';
              }}
            >
              {/* Tag */}
              <div style={{ fontFamily: 'monospace', fontSize: 8, color: `${card.accentColor}99`, letterSpacing: '.18em', textTransform: 'uppercase', marginBottom: 14 }}>
                {card.tag}
              </div>

              {/* Icon + Label row */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                <span style={{ fontSize: 'clamp(18px, 3vw, 24px)', color: card.accentColor, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>
                  {card.icon}
                </span>
                <div>
                  <div style={{ fontFamily: 'monospace', fontSize: 'clamp(11px, 2vw, 13px)', color: '#d4c5a9', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 3 }}>
                    {card.label}
                  </div>
                  <div style={{ fontFamily: "'Amiri', serif", fontSize: 'clamp(12px, 2.5vw, 15px)', color: `${card.accentColor}88`, lineHeight: 1.3 }}>
                    {card.arabic}
                  </div>
                </div>
              </div>

              {/* Description */}
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(11px, 2vw, 13px)', color: 'rgba(212,197,169,0.5)', lineHeight: 1.7, margin: 0 }}>
                {card.description}
              </p>

              {/* Arrow */}
              <div style={{ marginTop: 16, fontFamily: 'monospace', fontSize: 10, color: `${card.accentColor}66`, letterSpacing: '.1em' }}>
                Enter →
              </div>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .destination-cards-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 480px) {
          .destination-cards-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
